import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatNOK } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";
import { OfferLineRow } from "@/components/offer/OfferLineRow";
import { PHASE_LABEL } from "@/components/offer/offer-phase-tokens";
import type { OfferPhase } from "@/lib/offer-calc";
import type { UpsellLine } from "@/hooks/use-upsells";
import type { OfferLine } from "@/hooks/use-offers";
import { upsellLineToEditable, type EditableLine } from "@/lib/editable-line";

/** VIB-950: render mersalg-linje via den delte OfferLineRow. Adapter mapper
 *  upsell → EditableLine → OfferLine-form (rendring) og patch tilbake. */
function upsellToOfferRow(l: UpsellLine, hourlyRate: number): OfferLine {
  const e: EditableLine = upsellLineToEditable(l, hourlyRate);
  return {
    id: e.id,
    offer_id: "",
    template_id: null,
    phase: e.phase as OfferPhase,
    description: e.description,
    cost_type: e.cost_type,
    pricing_type: e.pricing_type,
    quantity: e.quantity,
    unit: e.unit,
    unit_price: e.unit_price,
    purchase_price: e.purchase_price,
    amount: e.amount,
    rate_basis_hourly: e.rate_basis_hourly,
    comment: e.comment,
    sort_order: e.sort_order,
    is_auto_generated: false,
    auto_kind: null,
    overtime_split: null,
    section_id: null,
  };
}

interface Props {
  phase: OfferPhase;
  lines: UpsellLine[];
  /** Per-linje fallback-sats: katalogens default_rate (via template_id), ellers
   *  system-`hourly_rate`. Brukes når amount/timer ikke selv kan bestemme raten. */
  resolveRate: (line: UpsellLine) => number;
  readOnly: boolean;
  onAddFromCatalog: () => void;
  onAddFreeText: () => void;
  onLineChange: (l: UpsellLine, patch: Partial<OfferLine>) => void;
  onLineDelete: (l: UpsellLine) => void;
}

export function UpsellPhaseSection({
  phase,
  lines,
  resolveRate,
  readOnly,
  onAddFromCatalog,
  onAddFreeText,
  onLineChange,
  onLineDelete,
}: Props) {
  const subtotal = lines.reduce((s, l) => s + (l.amount ?? 0), 0);
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="flex items-baseline gap-3">
            <h3 className="font-display text-base">{PHASE_LABEL[phase]}</h3>
            <span className="text-sm text-muted-foreground">{formatNOK(subtotal)}</span>
          </div>
          {!readOnly && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={onAddFromCatalog}>
                <SrIcon name="plus" className="w-3 h-3 mr-1" />
                Fra katalog
              </Button>
              <Button size="sm" variant="ghost" onClick={onAddFreeText}>
                Fritekst-linje
              </Button>
            </div>
          )}
        </div>

        {lines.length === 0 ? (
          <p className="text-sm text-muted-foreground py-3 text-center">
            Ingen linjer i denne fasen.
          </p>
        ) : (
          <div className="overflow-x-auto">
            {/* Kolonneheader speiler tilbudseditoren (VIB-950). */}
            <div className="hidden md:grid items-center px-1 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80 grid-cols-[24px_1fr_70px_72px_100px_100px_110px_28px] border-b">
              <span aria-hidden />
              <span className="pl-1">Linje</span>
              <span className="text-right pr-1.5">Mengde</span>
              <span className="pl-1">Enhet</span>
              <span className="text-right pr-1.5">Sats</span>
              <span className="text-right pr-1.5">Innkjøp</span>
              <span className="text-right pr-3">Beløp</span>
              <span aria-hidden />
            </div>
            {lines.map((l, i) => (
              <OfferLineRow
                key={l.id}
                line={upsellToOfferRow(l, resolveRate(l))}
                hourlyRate={resolveRate(l)}
                striped={i % 2 === 1}
                allowOvertime={false}
                readOnly={readOnly}
                onChange={(patch) => onLineChange(l, patch)}
                onDelete={() => onLineDelete(l)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
